import { useState, useEffect } from "react";
import { fetchModalRollTime } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Daily Mod Roll Time modal.
 *
 * Fires once on mount. Cancels the in-flight request automatically if the
 * component unmounts before the response arrives (AbortController cleanup).
 *
 * Both rollTimeMinutes (todaysRolls) and avgRollTime (last7BusinessDays) are
 * preserved as null when the backend returns null — callers should render "—"
 * rather than "0" for those cells (null = no calculable gap, not zero minutes).
 *
 * @returns {{
 *   todaysRolls: Array<{
 *     datetime:        string,
 *     rollTimeMinutes: number | null,  - null for the first roll of the day
 *     totalUnitCount:  number,
 *     lineLeader:      string | null,
 *   }>,
 *   last7BusinessDays: Array<{
 *     date:            string,
 *     avgRollTime:     number | null,  - null when <= 1 roll occurred that day
 *     avgUnitsPerRoll: number,
 *     lineLeaders:     string[],
 *   }>,
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

// ── Module-level cache ────────────────────────────────────────────────────────
// Shared across modal open/close cycles within the same browser session so
// that re-opening the modal does not re-fetch during the TTL window.
let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 60_000; // 1 minute — roll data updates infrequently

// ── Safe defaults ─────────────────────────────────────────────────────────────
// Returned during the loading phase so the modal tables render empty rows
// rather than crashing on undefined.
const DEFAULT_STATE = {
  todaysRolls: [],
  last7BusinessDays: [],
};

const useModRollsData = () => {
  const [todaysRolls, setTodaysRolls] = useState(
    _cache?.todaysRolls ?? DEFAULT_STATE.todaysRolls,
  );
  const [last7BusinessDays, setLast7BusinessDays] = useState(
    _cache?.last7BusinessDays ?? DEFAULT_STATE.last7BusinessDays,
  );
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const isStale = !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;
    if (!isStale) return; // serve from cache, skip fetch

    const load = async () => {
      try {
        const data = await fetchModalRollTime(controller.signal);
        _cache = data;
        _cacheTime = Date.now();
        setTodaysRolls(data.todaysRolls);
        setLast7BusinessDays(data.last7BusinessDays);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load mod roll time data.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  return { todaysRolls, last7BusinessDays, loading, error };
};

export default useModRollsData;
